import React, { useEffect, useMemo, type ReactNode, type CSSProperties } from 'react';
import { useCurrentRouteMetadata } from 'FrontRoyalRouteRegistry';
import { AnimatableRouteContent } from 'RouteAnimation';
// Import components and hooks from specific paths to avoid loading
// modules that have Angular dependencies that cause issues in test environments
import { AppHeaderReactWrapper } from 'AppHeaderReact/components/AppHeaderReactWrapper';

type Props = {
    children: ReactNode;
};

const APP_ROOT_NO_SHELL_CLASS = 'app-root-no-shell';

export const AppShell: React.FC<Props> = ({ children }) => {
    const routeMetadata = useCurrentRouteMetadata();
    const displaySettings = useShellDisplaySettings(routeMetadata);

    const content = displaySettings.shellVisible ? (
        <AnimatableRouteContent>{children}</AnimatableRouteContent>
    ) : (
        <>{children}</>
    );

    useEffect(() => {
        const appRoot = document.getElementById('app-root');
        if (!appRoot) return undefined;

        if (displaySettings.shellVisible) {
            appRoot.classList.remove(APP_ROOT_NO_SHELL_CLASS);
            return undefined;
        }

        appRoot.classList.add(APP_ROOT_NO_SHELL_CLASS);
        // Remove the class once the shell becomes visible again or on unmount.
        return () => {
            appRoot.classList.remove(APP_ROOT_NO_SHELL_CLASS);
        };
    }, [displaySettings.shellVisible]);

    return (
        <div id={displaySettings.shellVisible ? 'app-shell' : 'no-shell'}>
            <div style={displaySettings.shellStyle}>
                <AppHeaderReactWrapper />
            </div>
            <div
                id={displaySettings.shellVisible ? 'app-main-container' : undefined}
                className={displaySettings.containerClassName}
            >
                {content}
            </div>
            <div style={displaySettings.shellStyle}>
                {displaySettings.shellVisible ? (
                    <div className="front-royal-footer" style={{ display: 'none' }}>
                        <div className="content-wrapper" />
                    </div>
                ) : null}
            </div>
        </div>
    );
};

function useShellDisplaySettings(routeMetadata: ReturnType<typeof useCurrentRouteMetadata>) {
    const shellVisible = !routeMetadata?.removeAppShell;
    const headerLayout = routeMetadata?.headerLayout;
    const isLessonLayout = headerLayout === 'lesson-player';
    const showMobileMenu = shellVisible && !isLessonLayout;
    const shellStyle: CSSProperties | undefined = shellVisible ? undefined : { display: 'none' };
    const containerClassName = useMemo(() => {
        const classes = ['app-main-container'];
        if (isLessonLayout) classes.push('player');
        if (!showMobileMenu) classes.push('hide-mobile-menu');
        return classes.join(' ');
    }, [isLessonLayout, showMobileMenu]);

    return { shellVisible, showMobileMenu, shellStyle, containerClassName };
}
